import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context';
import { UserProfile } from './UserProfile';
import { LanguageSwitcher } from './LanguageSwitcher';
import { LoginModal } from './LoginModal';
import logo from '../assets/logo/logo.svg';

export const Navigation: React.FC = () => {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const location = useLocation();

  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
    };
    if (mobileMenuOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden'; // Prevent body scroll when menu is open
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [mobileMenuOpen]);

  const isActive = (path: string) => location.pathname === path;

  // Combine all links into one array
  const allLinks = [
    { to: '/', label: t('nav.home') },
    { to: '/about', label: t('nav.about') },
    { to: '/peoples', label: t('nav.people_places') },
    { to: '/edition', label: t('nav.edition') },
    { to: '/pilgrimages', label: t('nav.pilgrimages') },
    { to: '/links', label: t('nav.links') },
    { to: '/contact', label: t('nav.contact') },
  ];

  return (
    <>
      <nav className='relative top-0 left-0 right-0 z-20 bg-gradient-to-b from-black/80 via-black/60 to-transparent '>
        <div className='max-w-7xl mx-auto flex items-center justify-between px-3 sm:px-5'>
          {/* Logo */}
          <div className='flex items-center pr-2 sm:pr-4 md:pr-8 flex-shrink-0 '>
            <Link to='/' aria-label={t('nav.home')} className='inline-block' onClick={() => setMobileMenuOpen(false)}>
              <img
                src={logo}
                alt='Minihi Levenez'
                className='w-24 sm:w-32 md:w-56 h-10 sm:h-14 md:h-20 brightness-0 invert hover:opacity-90 transition'
                style={{ display: 'block' }}
              />
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className='md:hidden text-white p-2 hover:bg-white/10 rounded transition z-30 flex-shrink-0'
            aria-label='Toggle menu'
            aria-expanded={mobileMenuOpen}
          >
            {mobileMenuOpen ? (
              <svg
                className='w-6 h-6'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M6 18L18 6M6 6l12 12'
                />
              </svg>
            ) : (
              <svg
                className='w-6 h-6'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M4 6h16M4 12h16M4 18h16'
                />
              </svg>
            )}
          </button>

          {/* Desktop Menu + Actions */}
          {/* Fixed height row: children never wrap, `justify-between` lets the
              gaps shrink or grow to absorb differences in label length across
              languages instead of the navbar itself changing size. */}
          <div className='hidden md:flex items-center justify-between flex-nowrap gap-x-1 flex-1 min-w-0'>
            {allLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`flex-shrink-0 text-white text-xs lg:text-sm font-light px-2 lg:px-3 py-1.5 rounded transition-all hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/30 whitespace-nowrap ${
                  isActive(link.to) ? 'bg-white/10' : ''
                }`}
              >
                {link.label}
              </Link>
            ))}

            {/* Language + Auth */}
            <div className='flex items-center gap-2 lg:gap-3 flex-shrink-0'>
              <LanguageSwitcher />

              {isAuthenticated ? (
                <UserProfile compact />
              ) : (
                <button
                  onClick={() => setLoginModalOpen(true)}
                  aria-label={t('auth.login')}
                  className='inline-flex items-center justify-center w-8 h-8 lg:w-9 lg:h-9 rounded-full text-white bg-white/10 hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/30 transition flex-shrink-0'
                >
                  <svg
                    className='w-4 h-4 lg:w-5 lg:h-5'
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='currentColor'
                    strokeWidth='1.5'
                    aria-hidden='true'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      d='M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z'
                    />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <>
            {/* Backdrop */}
            <div 
              className='md:hidden fixed inset-0 bg-black/50 z-40 top-0'
              onClick={() => setMobileMenuOpen(false)}
            />
            {/* Menu Panel */}
            <div className='md:hidden absolute top-full left-0 right-0 bg-black/95 backdrop-blur-md z-50 border-t border-white/10 max-h-[calc(100vh-100px)] overflow-y-auto'>
              <div className='px-4 py-4 space-y-1'>
                {/* All Links */}
                {allLinks.map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`block px-4 py-3 rounded text-white text-base font-light transition ${
                      isActive(link.to) ? 'bg-white/20' : 'hover:bg-white/10'
                    }`}
                  >
                    {link.label}
                  </Link>
                ))}


                {/* Language + Auth */}
                <div className='border-t border-white/10 mt-4 pt-4 flex items-center justify-between gap-4'>
                  <LanguageSwitcher dropUp />
                  {isAuthenticated ? (
                    <UserProfile compact />
                  ) : (
                    <button
                      onClick={() => {
                        setMobileMenuOpen(false);
                        setLoginModalOpen(true);
                      }}
                      aria-label={t('auth.login')}
                      className='inline-flex items-center justify-center w-10 h-10 rounded-full text-white bg-white/10 hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/30 transition flex-shrink-0'
                    >
                      <svg
                        className='w-5 h-5'
                        viewBox='0 0 24 24'
                        fill='none'
                        stroke='currentColor'
                        strokeWidth='1.5'
                        aria-hidden='true'
                      >
                        <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          d='M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z'
                        />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </nav>

      <LoginModal
        isOpen={loginModalOpen}
        onClose={() => setLoginModalOpen(false)}
      />
    </>
  );
};
